// src/services/CreatorService.js

import { supabase, isDemoMode } from '../lib/supabase';

class CreatorService {
  // ============================================================
  // HANDLE AVAILABILITY
  // ============================================================
  async isHandleAvailable(handle) {
    if (isDemoMode) {
      return { available: true, error: null };
    }

    const normalized = handle.toLowerCase().trim();

    const { data: userData } = await supabase
      .from('users')
      .select('id')
      .eq('username', normalized)
      .limit(1);

    const { data: appData } = await supabase
      .from('creator_applications')
      .select('id')
      .eq('requested_handle', normalized)
      .eq('status', 'pending')
      .limit(1);

    const available = !userData?.length && !appData?.length;
    return { available, error: null };
  }

  // ============================================================
  // AVATAR UPLOAD
  // ============================================================
  /**
   * Upload an avatar image to Supabase Storage and return its public URL.
   * Called by the onboarding form when the creator picks a profile photo.
   *
   * - Bucket: `avatars` (must exist; configure RLS so authenticated users
   *   can upload files under their own wallet path, and reads are public).
   * - Filename pattern: `<wallet>-<timestamp>.<ext>` — the timestamp lets
   *   us upsert without colliding with prior uploads, and gives us a
   *   simple cache-bust on URL change.
   * - `upsert: true` overwrites if a file with the same name exists.
   *   Since the timestamp is in the name, that effectively never happens,
   *   but it's a safety net for race conditions.
   */
  async uploadAvatar(file, walletAddress) {
    if (isDemoMode) {
      return { url: 'https://placehold.co/256x256/1E1E1E/A3E635?text=Demo', error: null };
    }

    if (!file || !walletAddress) {
      return { url: null, error: new Error('Missing file or wallet address') };
    }

    const fileExt = (file.name.split('.').pop() || 'jpg').toLowerCase();
    const fileName = `${walletAddress.toLowerCase()}-${Date.now()}.${fileExt}`;
    const filePath = `avatars/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(filePath, file, {
        upsert: true,
        cacheControl: '3600',
        contentType: file.type,
      });

    if (uploadError) {
      console.error('[CreatorService] uploadAvatar error:', uploadError);
      return { url: null, error: uploadError };
    }

    const { data: { publicUrl } } = supabase.storage
      .from('avatars')
      .getPublicUrl(filePath);

    return { url: publicUrl, error: null };
  }

  // ============================================================
  // APPLICATION SUBMISSION
  // ============================================================
  /**
   * Submit a creator application from the onboarding flow.
   *
   * This method does two things in sequence:
   *   1. Upsert the `users` row from onboarding data — wallet-only auth
   *      means the user row may not exist yet, OR may have a stale
   *      username/avatar from a previous incomplete signup. We always
   *      write the freshly-entered values.
   *   2. Insert the application row, referencing the user_id.
   *
   * Identity fields (display_name, username, avatar_url, bio) live on
   * `users`. Creator-specific fields (content_description, socials,
   * creator_type, gender) live on `creator_applications` and later on
   * `creators` once approved.
   */
  async submitApplication(walletAddress, applicationData) {
    if (isDemoMode) {
      return {
        data: { id: 'demo-application', status: 'pending', ...applicationData },
        error: null,
      };
    }

    if (!walletAddress) {
      return { data: null, error: { message: 'Wallet not connected' } };
    }

    const walletLower = walletAddress.toLowerCase();
    const requestedHandle = (applicationData.requested_handle || '').toLowerCase().trim();

    if (!requestedHandle) {
      return { data: null, error: { message: 'Handle is required' } };
    }

    // ---------- STEP 1: upsert the users row ----------
    // Note: we DON'T overwrite `is_creator` here — that flag only flips to
    // true after admin approval. We only set identity fields the user
    // typed during onboarding.
    const userPayload = {
      wallet_address: walletLower,
      username: requestedHandle,
      display_name: applicationData.name || requestedHandle,
      avatar_url: applicationData.avatar_url || null,
      bio: applicationData.bio || null,
      updated_at: new Date().toISOString(),
    };

    // Server-side (Packet 04): the users row is written only by the
    // SIWE-checked user-profile function, for the signed-in wallet.
    const { userService } = await import('./UserService');
    const { data: userRow, error: userError } = await userService.updateProfile(walletLower, {
      username: userPayload.username,
      display_name: userPayload.display_name,
      avatar_url: userPayload.avatar_url,
      bio: userPayload.bio,
    });

    if (userError || !userRow) {
      console.error('[CreatorService] users profile update error:', userError);
      if (userError?.code === 'username_taken') {
        return { data: null, error: { message: 'This handle is already taken. Please choose another.' } };
      }
      return { data: null, error: userError || { message: 'Could not save your profile' } };
    }

    const userId = userRow.id;

    // ---------- STEP 2: guard against duplicate applications ----------
    const { data: existingApp } = await supabase
      .from('creator_applications')
      .select('id, status')
      .eq('user_id', userId)
      .in('status', ['pending', 'approved'])
      .limit(1);

    if (existingApp?.length) {
      const app = existingApp[0];
      if (app.status === 'pending') {
        return { data: null, error: { message: 'You already have a pending application' } };
      }
      if (app.status === 'approved') {
        return { data: null, error: { message: 'You are already an approved creator' } };
      }
    }

    const { data: existingCreator } = await supabase
      .from('creators')
      .select('id')
      .eq('user_id', userId)
      .eq('status', 'approved')
      .limit(1);

    if (existingCreator?.length) {
      return { data: null, error: { message: 'You are already an approved creator' } };
    }

    // ---------- STEP 3: insert the application ----------
    // Field map: form → application table
    //   name                  → name
    //   requested_handle      → requested_handle
    //   avatar_url            → avatar_url   (the freshly uploaded photo)
    //   bio                   → bio
    //   gender                → gender       (new — Discover filtering)
    //   creator_type          → creator_type
    //   location              → location     (now required)
    //   content_description   → content_description
    //   twitter_handle        → twitter_handle    (now just a string)
    //   instagram_handle      → instagram_handle
    //   tiktok_handle         → tiktok_handle
    //   website_url           → website_url
    //
    // Removed fields: twitter_id, twitter_followers, twitter_verified
    // (those came from OAuth which is no longer used).
    const { data, error } = await supabase
      .from('creator_applications')
      .insert({
        user_id: userId,
        // wallet_address: walletLower,   ← remove this line entirely

        requested_handle: requestedHandle,
        name: applicationData.name || requestedHandle,
        avatar_url: applicationData.avatar_url || null,
        bio: applicationData.bio || null,

        gender: applicationData.gender || null,
        creator_type: applicationData.creator_type || 'human',
        location: applicationData.location || null,
        content_description: applicationData.content_description || null,

        twitter_handle: applicationData.twitter_handle || null,
        instagram_handle: applicationData.instagram_handle || null,
        tiktok_handle: applicationData.tiktok_handle || null,
        website_url: applicationData.website_url || null,

        status: 'pending',
        created_at: new Date().toISOString(),
      })
      .select()
      .single();
    if (error) {
      console.error('[CreatorService] submitApplication insert error:', error);
      return { data: null, error };
    }

    return { data, error: null };
  }

  // ============================================================
  // APPLICATION LOOKUPS
  // ============================================================
  /**
   * Look up the most recent application for a wallet address.
   *
   * Used by the CreateModel page to detect existing applications and
   * show the appropriate status screen (pending / approved / rejected)
   * instead of the form. Wallet-only auth means we look up by wallet,
   * not by Twitter ID anymore.
   */
  async getApplicationByWallet(walletAddress) {
    if (isDemoMode) return { data: null, error: null };
    if (!walletAddress) return { data: null, error: null };

    // creator_applications has no wallet_address column — resolve via users.
    const { data: userRow } = await supabase
      .from('users')
      .select('id')
      .eq('wallet_address', walletAddress.toLowerCase())
      .maybeSingle();

    if (!userRow) return { data: null, error: null };

    const { data, error } = await supabase
      .from('creator_applications')
      .select('*')
      .eq('user_id', userRow.id)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('[CreatorService] getApplicationByWallet error:', error);
      return { data: null, error };
    }
    return { data, error: null };
  }

  /**
   * Legacy method — look up an application via the user_id derived from
   * wallet address. Kept for backward compatibility but new code should
   * use getApplicationByWallet (one fewer query).
   */
  async getApplicationStatus(walletAddress) {
    if (isDemoMode) {
      return { data: null, error: null };
    }

    const { data: userData } = await supabase
      .from('users')
      .select('id')
      .eq('wallet_address', walletAddress.toLowerCase())
      .limit(1);

    if (!userData?.length) {
      return { data: null, error: null };
    }

    const userId = userData[0].id;

    const { data, error } = await supabase
      .from('creator_applications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1);

    return { data: data?.[0] || null, error };
  }

  // ============================================================
  // CREATOR LOOKUPS (unchanged behaviour, just kept here)
  // ============================================================
  async getByHandle(handle) {
    if (isDemoMode) {
      return {
        data: null,
        error: null,
      };
    }

    const normalizedHandle = decodeURIComponent(
      String(handle),
    )
      .replace(/^@/, '')
      .trim()
      .toLowerCase();

    const { data, error } = await supabase
      .from('creators')
      .select('*')
      .eq('handle', normalizedHandle)
      .eq('status', 'approved')
      .maybeSingle();

    if (error) {
      console.error(
        'getByHandle error:',
        error,
      );

      return this._getByHandleFallback(
        normalizedHandle,
      );
    }

    return {
      data: data ?? null,
      error: null,
    };
  }

  async _getByHandleFallback(handle) {
    const normalizedHandle = decodeURIComponent(
      String(handle),
    )
      .replace(/^@/, '')
      .trim()
      .toLowerCase();

    const { data, error } = await supabase
      .from('users')
      .select(`
      id,
      wallet_address,
      username,
      display_name,
      avatar_url,
      bio,
      is_creator,
      created_at,
      creators!creators_user_id_fkey (
        id,
        status,
        contract_approved,
        banner_url,
        bio,
        twitter_handle,
        instagram_handle,
        tiktok_handle,
        website_url,
        content_description,
        location,
        creator_type,
        gender,
        follower_count,
        total_earnings,
        created_at,
        updated_at
      )
    `)
      .eq('username', normalizedHandle)
      .limit(1);

    if (error || !data?.length) {
      if (error) {
        console.error(
          '_getByHandleFallback error:',
          error,
        );
      }

      return {
        data: null,
        error,
      };
    }

    const user = data[0];
    const creator = user.creators?.[0];

    if (
      !creator ||
      creator.status !== 'approved'
    ) {
      return {
        data: null,
        error: null,
      };
    }

    return {
      data: {
        creator_id: creator.id,
        user_id: user.id,
        wallet_address: user.wallet_address,
        handle: user.username,
        name: user.display_name,
        avatar_url: user.avatar_url,

        // The creators table is authoritative.
        // users.bio remains a legacy fallback.
        bio:
          creator.bio ??
          user.bio ??
          '',

        banner_url: creator.banner_url,
        twitter_handle:
          creator.twitter_handle,
        instagram_handle:
          creator.instagram_handle,
        tiktok_handle:
          creator.tiktok_handle,
        website_url:
          creator.website_url,
        content_description:
          creator.content_description,
        location:
          creator.location,
        creator_type:
          creator.creator_type,
        gender:
          creator.gender,
        creator_status:
          creator.status,
        contract_approved:
          creator.contract_approved,
        is_creator:
          user.is_creator,
        follower_count:
          creator.follower_count,
        total_earnings:
          creator.total_earnings,
        user_created_at:
          user.created_at,
        created_at:
          creator.created_at,
        updated_at:
          creator.updated_at,
      },
      error: null,
    };
  }

  /**
   * The creator OWNED by a users row (creators.user_id). The platform
   * allows exactly one creator (AI or human) per human account, so this
   * is a single-row lookup. Used for profile tagging: an AI persona's
   * creators row links to the owner's user_id while its own
   * wallet_address stays null until the agent wallet is provisioned.
   */
  async getByUserId(userId) {
    if (isDemoMode || !userId) {
      return { data: null, error: null };
    }

    const { data, error } = await supabase
      .from('creators')
      .select('*')
      .eq('user_id', userId)
      .eq('status', 'approved')
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('getByUserId error:', error);
      return { data: null, error };
    }

    return { data: data ?? null, error: null };
  }

  async getByWalletAddress(walletAddress) {
    if (isDemoMode) {
      return { data: null, error: null };
    }

    const { data, error } = await supabase
      .rpc('get_creator_by_wallet', { p_wallet_address: walletAddress.toLowerCase() });

    if (error) {
      console.error('getByWalletAddress error:', error);
      return this._getByWalletFallback(walletAddress);
    }

    // EMPTY ≠ not-a-creator: AI-creator OWNERS don't match on wallet
    // (creators.wallet_address is the agent's contract account, or null
    // for API-provisioned personas). The fallback resolves ownership via
    // users.wallet → creators.user_id — without this, owners of
    // admin-created personas could never open Studio.
    const row = data?.[0] || null;
    if (!row) return this._getByWalletFallback(walletAddress);

    return { data: row, error: null };
  }

  async _getByWalletFallback(walletAddress) {
    const { data, error } = await supabase
      .from('users')
      .select(`
        id,
        wallet_address,
        username,
        display_name,
        avatar_url,
        bio,
        is_creator,
        creators!creators_user_id_fkey (
          id,
          status,
          contract_approved,
          banner_url,
          twitter_handle,
          instagram_handle,
          tiktok_handle,
          website_url,
          content_description,
          location,
          creator_type,
          gender,
          agent_id,
          is_ai,
          follower_count,
          total_earnings
        )
      `)
      .eq('wallet_address', walletAddress.toLowerCase())
      .limit(1);

    if (error || !data?.length) {
      return { data: null, error };
    }

    const user = data[0];
    const creator = user.creators?.[0];

    if (!creator || creator.status !== 'approved') {
      return {
        data: {
          user_id: user.id,
          wallet_address: user.wallet_address,
          handle: user.username,
          name: user.display_name,
          avatar_url: user.avatar_url,
          bio: user.bio,
          is_creator: user.is_creator,
          creator_id: null,
          creator_status: creator?.status || null,
          contract_approved: false,
        },
        error: null
      };
    }

    return {
      data: {
        creator_id: creator.id,
        user_id: user.id,
        wallet_address: user.wallet_address,
        handle: user.username,
        name: user.display_name,
        avatar_url: user.avatar_url,
        bio: user.bio,
        banner_url: creator.banner_url,
        twitter_handle: creator.twitter_handle,
        instagram_handle: creator.instagram_handle,
        tiktok_handle: creator.tiktok_handle,
        website_url: creator.website_url,
        content_description: creator.content_description,
        location: creator.location,
        creator_type: creator.creator_type,
        gender: creator.gender,
        creator_status: creator.status,
        contract_approved: creator.contract_approved,
        is_creator: user.is_creator,
        follower_count: creator.follower_count,
        total_earnings: creator.total_earnings,
      },
      error: null,
    };
  }

  async getById(id) {
    if (isDemoMode) {
      return { data: null, error: null };
    }

    const { data, error } = await supabase
      .from('creators')
      .select(`
        *,
        users!creators_user_id_fkey (
          wallet_address,
          username,
          display_name,
          avatar_url,
          bio
        )
      `)
      .eq('id', id)
      .single();

    if (error || !data) {
      return { data: null, error };
    }

    const user = data.users;
    return {
      data: {
        ...data,
        handle: user?.username || data.handle,
        name: user?.display_name || data.name,
        avatar_url: user?.avatar_url || data.avatar_url,
        bio: user?.bio || data.bio,
        wallet_address: user?.wallet_address,
      },
      error: null,
    };
  }

  async getApprovedCreators({ limit = 50, offset = 0, search = '' } = {}) {
    if (isDemoMode) {
      return { data: [], error: null };
    }

    let query = supabase
      .from('creators')
      .select(`
        *,
        users!creators_user_id_fkey (
          wallet_address,
          username,
          display_name,
          avatar_url,
          bio
        )
      `)
      .eq('status', 'approved')
      .order('follower_count', { ascending: false })
      .range(offset, offset + limit - 1);

    if (search) {
      query = query.or(`handle.ilike.%${search}%,name.ilike.%${search}%`);
    }

    const { data, error } = await query;

    if (error) {
      return { data: [], error };
    }

    const creators = (data || []).map(creator => {
      // An AI persona's public identity is its CREATOR row — never the
      // owner's users row (an assigned owner's own name/avatar/handle must
      // not replace the persona's on Discover).
      const user = creator.is_ai === true ? null : creator.users;
      return {
        ...creator,
        handle: user?.username || creator.handle,
        name: user?.display_name || creator.name,
        avatar_url: user?.avatar_url || creator.avatar_url,
        bio: user?.bio || creator.bio,
        // WALLET RULE (P8): the CREATOR row's wallet wins. For an AI
        // persona that is the agent ACCOUNT — the address the founding
        // series is registered against, so every card-market lookup
        // (Discover pills, series stats) must key on it. The old
        // owner-wallet override here silently pointed Liv's Discover
        // card at her human owner's wallet, which has no series —
        // every launched drop read "Soon". Owner wallet remains the
        // fallback for legacy human creators whose creators row never
        // carried a wallet.
        wallet_address: creator.wallet_address || user?.wallet_address,
      };
    });

    return { data: creators, error: null };
  }

  async getAllCreators(options = {}) {
    return this.getApprovedCreators(options);
  }

  // ============================================================
  // CREATOR PROFILE UPDATES
  // ============================================================
  async updateCreatorProfile(creatorId, updates) {
    if (isDemoMode) {
      return { data: { id: creatorId, ...updates }, error: null };
    }

    const allowedFields = [
      'banner_url',
      'twitter_handle',
      'instagram_handle',
      'tiktok_handle',
      'website_url',
      'content_description',
      'location',
      'gender',
    ];

    const safeUpdates = {};
    for (const field of allowedFields) {
      if (updates[field] !== undefined) {
        safeUpdates[field] = updates[field];
      }
    }

    safeUpdates.updated_at = new Date().toISOString();

    const { data, error } = await supabase
      .from('creators')
      .update(safeUpdates)
      .eq('id', creatorId)
      .select()
      .single();

    return { data, error };
  }

  async updateProfile(creatorId, updates) {
    console.warn('CreatorService.updateProfile is deprecated. Use updateCreatorProfile for creator fields.');
    return this.updateCreatorProfile(creatorId, updates);
  }

  async setContractApproved(creatorId, txHash) {
    if (isDemoMode) {
      return { data: { id: creatorId, contract_approved: true }, error: null };
    }

    const { data, error } = await supabase
      .from('creators')
      .update({
        contract_approved: true,
        contract_approved_at: new Date().toISOString(),
        contract_tx_hash: txHash,
      })
      .eq('id', creatorId)
      .select()
      .single();

    return { data, error };
  }

  async setOnlineStatus(creatorId, isOnline) {
    if (isDemoMode) {
      return { error: null };
    }

    const { error } = await supabase
      .from('creators')
      .update({
        is_online: isOnline,
        last_seen_at: isOnline ? null : new Date().toISOString(),
      })
      .eq('id', creatorId);

    return { error };
  }

  // ============================================================
  // ADMIN FUNCTIONS
  // ============================================================
  async getPendingApplications({ limit = 50, offset = 0 } = {}) {
    const { data, error } = await supabase
      .from('creator_applications')
      .select(`
        *,
        users!creator_applications_user_id_fkey (
          username,
          display_name,
          wallet_address,
          avatar_url
        )
      `)
      .eq('status', 'pending')
      .order('created_at', { ascending: true })
      .range(offset, offset + limit - 1);

    return { data: data || [], error };
  }

  /**
   * Approve an application and create the creator row.
   *
   * Identity fields (handle, name, avatar_url, bio) come from the
   * APPLICATION row, not the users row — the user row should already have
   * been updated to match during submitApplication, but the application is
   * the canonical snapshot of what was approved (in case the user later
   * edits their profile).
   *
   * Creator-specific fields (content_description, socials, creator_type,
   * gender, location) come from the application row as well.
   */

  /**
   * Create a fresh AI agent row for a creator and return its id.
   * No character yet (the creator builds that in the Persona tab) and no
   * api_key in config — on-message falls back to the platform ANTHROPIC_API_KEY
   * env var, so provider keys never live in the database.
   */
  async _createAgentForCreator({ name = 'Creator', handle = null, provider = 'anthropic', ownerUserId = null } = {}) {
    const { data: agent, error } = await supabase
      .from('agents')
      .insert({
        name,
        handle,
        agent_type: provider,
        is_active: true,
        agent_config: { provider, model: null },
        // Authorization root: review-scenes / schedule-episode /
        // agent-policy all gate on agents.owner_id → users.id. Without it
        // every owner-gated surface rejects the real owner.
        owner_id: ownerUserId,
      })
      .select('id')
      .single();

    if (error || !agent) {
      return { agentId: null, error: error || { message: 'Failed to create agent' } };
    }
    return { agentId: agent.id, error: null };
  }

  async approveApplication(applicationId, adminUserId) {
    const { data: app, error: fetchError } = await supabase
      .from('creator_applications')
      .select(`
        *,
        users!creator_applications_user_id_fkey (
          id,
          username,
          display_name,
          wallet_address,
          avatar_url,
          bio
        )
      `)
      .eq('id', applicationId)
      .single();

    if (fetchError || !app) {
      return { data: null, error: fetchError || { message: 'Application not found' } };
    }

    const user = app.users;
    if (!user) {
      return { data: null, error: { message: 'User not found for this application' } };
    }
    if (!user.wallet_address) {
      return { data: null, error: { message: 'User has no wallet address on file — cannot approve.' } };
    }

    // Identity for the agent row (satisfies agents.name / agents.handle NOT NULL).
    const agentName = app.name || app.requested_handle;
    const agentHandle = app.requested_handle;

    // Already a creator? Ensure they have an agent, then just update status.
    const { data: existingCreator } = await supabase
      .from('creators')
      .select('id, agent_id')
      .eq('user_id', user.id)
      .single();

    if (existingCreator) {
      // Backfill: older creators may have been created before agents were linked.
      if (!existingCreator.agent_id) {
        const { agentId, error: agentErr } = await this._createAgentForCreator({
          name: agentName, handle: agentHandle, provider: 'anthropic',
          ownerUserId: user.id,
        });
        if (agentErr) return { data: null, error: agentErr };
        await supabase.from('creators').update({ agent_id: agentId }).eq('id', existingCreator.id);
      }

      await supabase
        .from('creator_applications')
        .update({
          status: 'approved',
          reviewed_at: new Date().toISOString(),
          reviewed_by: adminUserId,
          review_notes: 'Creator profile already exists',
        })
        .eq('id', applicationId);

      return { data: existingCreator, error: null };
    }

    // New creator: create the agent FIRST so the creator is never created
    // without one (the dead-creator bug). Roll the agent back if creator fails.
    const { agentId, error: agentErr } = await this._createAgentForCreator({
      name: agentName, handle: agentHandle, provider: 'anthropic',
      ownerUserId: user.id,
    });
    if (agentErr || !agentId) {
      return { data: null, error: agentErr || { message: 'Could not create AI agent for creator' } };
    }

    const { data: creator, error: createError } = await supabase
      .from('creators')
      .insert({
        user_id: user.id,
        wallet_address: user.wallet_address?.toLowerCase(),
        agent_id: agentId,
        is_ai: creator.is_ai,

        // Identity snapshot from the application (mirrors users row at submit time)
        handle: app.requested_handle,
        name: app.name,
        avatar_url: app.avatar_url,
        bio: app.bio,

        // Creator-specific fields
        banner_url: app.banner_url || null,
        twitter_handle: app.twitter_handle,
        instagram_handle: app.instagram_handle,
        tiktok_handle: app.tiktok_handle,
        website_url: app.website_url,
        content_description: app.content_description,
        creator_type: app.creator_type,
        gender: app.gender,
        location: app.location,

        // Status
        status: 'approved',
        contract_approved: false,
        approved_at: new Date().toISOString(),
        approved_by: adminUserId,
      })
      .select()
      .single();

    if (createError) {
      // Roll back the orphaned agent so we don't leak rows.
      await supabase.from('agents').delete().eq('id', agentId);
      return { data: null, error: createError };
    }

    await supabase
      .from('creator_applications')
      .update({
        status: 'approved',
        reviewed_at: new Date().toISOString(),
        reviewed_by: adminUserId,
      })
      .eq('id', applicationId);

    await supabase
      .from('users')
      .update({ is_creator: true })
      .eq('id', user.id);

    return {
      data: {
        ...creator,
        wallet_address: user.wallet_address,
        handle: app.requested_handle,
        name: app.name,
      },
      error: null,
    };
  }

  async rejectApplication(applicationId, adminUserId, reason) {
    const { data, error } = await supabase
      .from('creator_applications')
      .update({
        status: 'rejected',
        reviewed_at: new Date().toISOString(),
        reviewed_by: adminUserId,
        review_notes: reason,
      })
      .eq('id', applicationId)
      .select()
      .single();

    return { data, error };
  }

  // ============================================================
  // BATCH USER / MARKET STATS LOOKUPS (unchanged)
  // ============================================================

  /**
   * Batch creators-by-wallet, CASE-PROOF. creators.wallet_address casing
   * is inconsistent (some rows checksummed) and PostgREST .in() is
   * case-sensitive, so we fetch the (small) creator set and match
   * client-side on lowercase. Returns Map<lowercaseWallet, creatorRow>.
   */
  async getCreatorsByWallets(walletAddresses) {
    const wanted = new Set(
      (walletAddresses || []).filter(Boolean).map((a) => a.toLowerCase())
    );
    if (wanted.size === 0) return new Map();

    try {
      const { data, error } = await supabase
        .from('creators')
        .select('wallet_address, handle, name, avatar_url')
        .not('wallet_address', 'is', null)
        .limit(1000);

      if (error) {
        console.error('[CreatorService] getCreatorsByWallets error:', error);
        return new Map();
      }

      const map = new Map();
      for (const row of data || []) {
        const key = row.wallet_address?.toLowerCase();
        if (key && wanted.has(key)) map.set(key, row);
      }
      return map;
    } catch (err) {
      console.error('[CreatorService] getCreatorsByWallets exception:', err);
      return new Map();
    }
  }

  async getUsersByWallets(walletAddresses) {
    if (!walletAddresses || walletAddresses.length === 0) {
      return new Map();
    }

    const unique = [...new Set(
      walletAddresses.filter(Boolean).map(a => a.toLowerCase())
    )];

    if (unique.length === 0) return new Map();

    try {
      const { data, error } = await supabase
        .from('users')
        .select('wallet_address, username, display_name, avatar_url')
        .in('wallet_address', unique);

      if (error) {
        console.error('[CreatorService] getUsersByWallets error:', error);
        return new Map();
      }

      const map = new Map();
      for (const row of data || []) {
        if (row.wallet_address) {
          map.set(row.wallet_address.toLowerCase(), row);
        }
      }
      return map;
    } catch (err) {
      console.error('[CreatorService] getUsersByWallets exception:', err);
      return new Map();
    }
  }

  // NOTE (P8): the other xpass_trades readers (market stats, holders,
  // trade history, recordXPassTrade) were deleted with the bonding curve;
  // this one survives because CreatorDashboard still sums LEGACY curve-era
  // trading fees in Earnings. Swap to card royalties when that tab is
  // reworked.
  async getXPassTradingFees(creatorWalletAddress) {
    if (!creatorWalletAddress) {
      return { totalFees: 0, totalFeesWei: '0', tradeCount: 0 };
    }

    const creatorLower = creatorWalletAddress.toLowerCase();
    const PAGE_SIZE = 1000;

    try {
      let totalFeesWei = 0n;
      let tradeCount = 0;
      let offset = 0;

      while (true) {
        const { data, error } = await supabase
          .from('xpass_trades')
          .select('creator_fee')
          .eq('creator', creatorLower)
          .range(offset, offset + PAGE_SIZE - 1);

        if (error) {
          console.error('[CreatorService] getXPassTradingFees error:', error);
          return { totalFees: 0, totalFeesWei: '0', tradeCount: 0, error };
        }

        if (!data || data.length === 0) break;

        for (const row of data) {
          try {
            totalFeesWei += BigInt(row.creator_fee || '0');
            tradeCount += 1;
          } catch {
            // skip malformed
          }
        }

        if (data.length < PAGE_SIZE) break;
        offset += PAGE_SIZE;
      }

      const ONE_ETH = 10n ** 18n;
      const ethPart = totalFeesWei / ONE_ETH;
      const weiRemainder = totalFeesWei % ONE_ETH;
      const decimalsStr = weiRemainder.toString().padStart(18, '0').replace(/0+$/, '');
      const totalFeesStr = decimalsStr.length > 0
        ? `${ethPart}.${decimalsStr}`
        : `${ethPart}`;
      const totalFees = parseFloat(totalFeesStr);

      return {
        totalFees,
        totalFeesWei: totalFeesWei.toString(),
        tradeCount,
      };
    } catch (err) {
      console.error('[CreatorService] getXPassTradingFees exception:', err);
      return { totalFees: 0, totalFeesWei: '0', tradeCount: 0, error: err };
    }
  }

}

export const creatorService = new CreatorService();
export default creatorService;